import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, CheckCircle, AlertCircle, Loader2, LucideClipboardSignature } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { dbService } from '../lib/supabase';

interface ReviewFormData {
  overall_rating: number;
  responsiveness_rating: number;
  communication_rating: number;
  alignment_rating?: number;
  professionalism_rating?: number;
  requirements_clarity_rating?: number;
  text_feedback: string;
}
interface ReviewFormSubmission {
  introduction_request_id: string;
        reviewer_user_id: string;
        reviewer_type: 'startup' | 'cdmo';
        overall_rating: number;
        responsiveness_rating: number;
        communication_rating: number;
        text_feedback: string;
        alignment_rating?: number;
        reviewed_cdmo_id?: string;
        professionalism_rating?: number;
        requirements_clarity_rating?: number;
        reviewed_user_id?: string;
}

interface IntroductionRequest {
  id: string;
  requester_user_id: string;
  requester_name: string;
  requester_email: string;
  cdmo_id: string;
  status: string;
  created_at: string;
  cdmo_applications?: {
    company_name: string;
    contact_name: string;
    user_id: string;
  };
}

const ReviewPage: React.FC = () => {
  const { requestId, reviewerType } = useParams<{ requestId: string; reviewerType: 'startup' | 'cdmo' }>();
  const navigate = useNavigate();
  const { user , loading} = useAuth();
  
  const [loadingReq, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [introRequest, setIntroRequest] = useState<IntroductionRequest | null>(null);
  const [formData, setFormData] = useState<ReviewFormData>({
    overall_rating: 0,
    responsiveness_rating: 0,
    communication_rating: 0,
    text_feedback: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ErrorMessage, setErrorMessage] = useState<string>('Review Not Available');

 useEffect(() => {
  if (loading) return; // wait until auth finishes loading

  if (!requestId || !reviewerType) {
    setError('Invalid review link.');
    setLoading(false);
    return;
  }

  loadIntroductionRequest();
}, [requestId, reviewerType, loading]);



  const loadIntroductionRequest = async () => {
    try {
      setLoading(true);
      
      // Get introduction request
      const request = await dbService.getIntroductionRequestById(requestId!);
      
      if (!request) {
        setErrorMessage('Request Not Found');
        setError('Introduction request not found.');
        return;
      }

      if (request.status !== 'accepted') {
        setErrorMessage('Invalid Request Status');
        setError('Reviews can only be submitted for accepted introduction requests.');
        return;
      }

      // Verify user permission
      const canReview = await verifyReviewPermission(request);

      if (!canReview) {
        setErrorMessage('Permission Denied');
        setError('You do not have permission to submit this review.');
        return;
      }

      // Check if review already exists
      const existingReview = await dbService.getReviewByRequestAndReviewer(
        requestId!, 
        user!.id, 
        reviewerType!
      );
      if (existingReview) {
        setErrorMessage('Review Already Submitted');
        setError('You have already submitted a review for this introduction.');
        return;
      }

      setIntroRequest(request);
    } catch (err) {
      console.error('Error loading introduction request:', err);
      setError('Failed to load introduction request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyReviewPermission = async (request: IntroductionRequest): Promise<boolean> => {
    if (reviewerType === 'startup') {
      return user!.id === request.requester_user_id;
    } else if (reviewerType === 'cdmo') {
      // Check if user owns this CDMO application
      return await dbService.isCdmoUser(user!.id, request.cdmo_id);
    }
    return false;
  };

  const handleRatingChange = (field: keyof ReviewFormData, rating: number) => {
    const cappedRating = Math.min(Math.max(rating, 0), 5); // ensure rating is between 1 and 5
    setFormData(prev => ({ ...prev, [field]: cappedRating }));
  };

  const validateForm = (): boolean => {
    if (formData.overall_rating === 0 || formData.responsiveness_rating === 0 || formData.communication_rating === 0) {
      setError('Please provide ratings for all required fields.');
      return false;
    }

    if (reviewerType === 'startup' && !formData.alignment_rating) {
      setError('Please provide an alignment rating.');
      return false;
    }

    if (reviewerType === 'cdmo' && (!formData.professionalism_rating || !formData.requirements_clarity_rating)) {
      setError('Please provide professionalism and requirements clarity ratings.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!introRequest || !user) return;

    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      const reviewData: ReviewFormSubmission = {
        introduction_request_id: introRequest.id,
        reviewer_user_id: user.id,
        reviewer_type: reviewerType!,
        overall_rating: formData.overall_rating,
        responsiveness_rating: formData.responsiveness_rating,
        communication_rating: formData.communication_rating,
        text_feedback: formData.text_feedback
      };

      // Add type-specific data
      if (reviewerType === 'startup') {
        // Startup reviewing CDMO
        reviewData.alignment_rating = formData.alignment_rating;
        reviewData.reviewed_cdmo_id = introRequest.cdmo_id;
      } else {
        // CDMO reviewing startup
        reviewData.professionalism_rating = formData.professionalism_rating;
        reviewData.requirements_clarity_rating = formData.requirements_clarity_rating;
        reviewData.reviewed_user_id = introRequest.requester_user_id;
      }

      await dbService.createReview(reviewData);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating: React.FC<{ 
    rating: number; 
    onRatingChange: (rating: number) => void; 
    label: string;
    required?: boolean;
  }> = ({ rating, onRatingChange, label, required = false }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            className="focus:outline-none transition-colors"
          >
            <Star
              className={`w-6 h-6 ${
                star <= rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300 hover:text-yellow-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (loadingReq) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-blue/20 to-deep-indigo/10 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-deep-indigo mx-auto mb-4" />
          <p className="text-slate-600">Loading review form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-blue/20 to-deep-indigo/10 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-4">{ErrorMessage}</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-deep-indigo text-white px-6 py-2 rounded-lg hover:bg-deep-indigo/90 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-blue/20 to-deep-indigo/10 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4"
        >
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Review Submitted!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your feedback! Your review helps improve our matching process.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-deep-indigo text-white px-6 py-2 rounded-lg hover:bg-deep-indigo/90 transition-colors"
            >
              Return Home
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const isStartupReview = reviewerType === 'startup';
  const reviewTarget = isStartupReview 
    ? introRequest?.cdmo_applications?.company_name 
    : introRequest?.requester_name;

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-blue/20 to-deep-indigo/10 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">
              Review Your Experience
            </h1>
            <p className="text-slate-600">
              Please share your feedback about your interaction with{' '}
              <span className="font-semibold text-deep-indigo">{reviewTarget}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <StarRating
              rating={formData.overall_rating}
              onRatingChange={(rating) => handleRatingChange('overall_rating', rating)}
              label={`Overall ${isStartupReview ? 'Satisfaction' : 'Experience'}`}
              required
            />

            <StarRating
              rating={formData.responsiveness_rating}
              onRatingChange={(rating) => handleRatingChange('responsiveness_rating', rating)}
              label="Responsiveness"
              required
            />

            <StarRating
              rating={formData.communication_rating}
              onRatingChange={(rating) => handleRatingChange('communication_rating', rating)}
              label="Clarity of Communication"
              required
            />

            {isStartupReview && (
              <StarRating
                rating={formData.alignment_rating || 0}
                onRatingChange={(rating) => handleRatingChange('alignment_rating', rating)}
                label="Alignment with Project Needs"
                required
              />
            )}

            {!isStartupReview && (
              <>
                <StarRating
                  rating={formData.professionalism_rating || 0}
                  onRatingChange={(rating) => handleRatingChange('professionalism_rating', rating)}
                  label="Professionalism"
                  required
                />
                <StarRating
                  rating={formData.requirements_clarity_rating || 0}
                  onRatingChange={(rating) => handleRatingChange('requirements_clarity_rating', rating)}
                  label="Clarity of Project Requirements"
                  required
                />
              </>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Additional Feedback (Optional)
              </label>
              <textarea
                value={formData.text_feedback}
                onChange={(e) => setFormData(prev => ({ ...prev, text_feedback: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-deep-indigo/20 focus:border-deep-indigo"
                placeholder="Please share any additional thoughts about your experience..."
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-deep-indigo text-white py-3 rounded-lg font-semibold hover:bg-deep-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Submitting Review...
                </>
              ) : (
                'Submit Review'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ReviewPage; 